import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCirclePlus, faPen, faTrash, faXmark } from '@fortawesome/free-solid-svg-icons';
import { BP_TOOLBAR_BUTTON_CLASSES } from '../../Components/brandPresenceClasses';

interface IGovernanceOnboardingContainerProps {
  children: React.ReactNode;
}

export function GovernanceOnboardingContainer({ children }: IGovernanceOnboardingContainerProps) {
  return (
    <div className="relative min-h-full w-full bg-slate-50 p-6 md:p-10 bg-brand-page">
      <div className="relative h-full w-full flex items-center justify-center">
        <div className="w-full max-w-4xl rounded-2xl bg-white shadow-card ring-1 ring-slate-200/70 motion-safe:animate-bp-fade-up">
          {children}
        </div>
      </div>
    </div>
  );
}

interface IGovernanceOnboardingStepContainerProps {
  step: number;
  totalSteps: number;
  title: string;
  description: string;
  children: React.ReactNode;
  onBack?: () => void;
  onContinue?: () => void;
  continueLabel?: string;
  backLabel?: string;
  footerRight?: React.ReactNode;
  continueDisabled?: boolean;
}

export function GovernanceOnboardingStepContainer({
  step,
  totalSteps,
  title,
  description,
  children,
  onBack,
  onContinue,
  continueLabel = 'Continue',
  backLabel = 'Back',
  footerRight,
  continueDisabled,
}: IGovernanceOnboardingStepContainerProps) {
  const progressPercent = Math.round((step / totalSteps) * 100);

  return (
    <div className="p-8 md:px-12 md:py-10">
      <div className="pb-10">
        <div className="flex justify-between items-end mb-4">
          <span className="flex items-center gap-2 font-eyebrow text-[0.6875rem] font-medium uppercase tracking-[0.16em] text-slate-500">
            <span aria-hidden="true" className="inline-block h-[3px] w-5 rounded-full bg-brand-gradient" />
            Step {step} of {totalSteps}
          </span>
          <span className="text-sm font-semibold tabular-nums text-slate-500">{progressPercent}% Complete</span>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
          {/* The width is the progress through the wizard, known only at runtime. */}
          <div
            className="h-full rounded-full bg-brand-gradient transition-all duration-500 ease-out"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      <div className="text-center mb-10">
        <h2 className="mb-3 text-3xl font-bold tracking-tight text-slate-900 [text-wrap:balance]">{title}</h2>
        <p className="mx-auto max-w-2xl text-center text-[0.9375rem] leading-relaxed text-slate-500">{description}</p>
      </div>

      <div>{children}</div>

      <div className="border-t border-slate-200/80 pt-8 mt-8 flex items-center justify-between">
        <button type="button" onClick={onBack} className={BP_TOOLBAR_BUTTON_CLASSES}>
          {backLabel}
        </button>
        <div className="flex items-center gap-4">
          {footerRight}
          <button
            type="button"
            onClick={onContinue}
            disabled={continueDisabled}
            className="rounded-lg px-8 py-3 bg-brand-gradient font-semibold text-white shadow-sm transition-all duration-150 hover:shadow-card-hover hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {continueLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

interface IGovernanceAiSuggestionLabelProps {
  children?: React.ReactNode;
}

export function GovernanceAiSuggestionLabel({ children = 'AI Suggestion' }: IGovernanceAiSuggestionLabelProps) {
  return (
    <span className="inline-flex shrink-0 whitespace-nowrap rounded-full border border-slate-200 bg-brand-gradient bg-clip-text px-2.5 py-0.5 font-eyebrow text-[10px] font-semibold uppercase tracking-[0.12em] text-transparent">
      {children}
    </span>
  );
}

interface IGovernanceAiSuggestedListItemProps {
  children: React.ReactNode;
  onAccept: () => void;
  onIgnore: () => void;
}

export function GovernanceAiSuggestedListItem({ children, onAccept, onIgnore }: IGovernanceAiSuggestedListItemProps) {
  return (
    <div className="relative grid grid-cols-12 gap-4 items-center overflow-hidden rounded-r-xl rounded-l-sm border border-slate-200/80 bg-slate-50/70 p-4 shadow-sm motion-safe:animate-bp-fade-in">
      <div aria-hidden="true" className="absolute left-0 top-0 bottom-0 w-1 rounded-s-lg bg-brand-gradient" />
      {children}
      <div className="col-span-12 md:col-span-2 flex justify-end items-center space-x-3 mt-2 md:mt-0 pl-2 md:pl-0">
        <button
          type="button"
          onClick={onAccept}
          className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-white shadow-sm transition-all duration-150 hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-1"
          title="Accept"
        >
          <FontAwesomeIcon icon={faCheck} />
        </button>
        <button
          type="button"
          onClick={onIgnore}
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-colors duration-150 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          title="Ignore"
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>
    </div>
  );
}

interface IGovernanceOnboardingListItemProps {
  children: React.ReactNode;
  onEdit?: () => void;
  onDelete?: () => void;
  /** What the delete button is announced as, naming the row, e.g. "Remove English (United States)". */
  deleteLabel?: string;
}

export function GovernanceOnboardingListItem({ children, onEdit, onDelete, deleteLabel }: IGovernanceOnboardingListItemProps) {
  const [isDeleteConfirmationPending, setIsDeleteConfirmationPending] = React.useState(false);

  const handleDeleteClick = () => {
    if (!onDelete) {
      return;
    }

    if (isDeleteConfirmationPending) {
      onDelete();
      return;
    }

    setIsDeleteConfirmationPending(true);
  };

  return (
    <div className="grid grid-cols-12 gap-4 items-center rounded-xl border border-slate-200/80 bg-white p-4 shadow-sm transition-shadow duration-200 hover:shadow-card-hover">
      {children}
      <div className="col-span-12 md:col-span-2 flex justify-end space-x-2 text-slate-400 mt-2 md:mt-0">
        {onEdit ? (
          <button type="button" onClick={onEdit} className="hover:text-primary transition-colors" title="Edit">
            <FontAwesomeIcon icon={faPen} />
          </button>
        ) : null}
        {onDelete ? (
          <button
            type="button"
            onClick={handleDeleteClick}
            className="hover:text-red-500 transition-colors relative"
            title={isDeleteConfirmationPending ? 'Click again to confirm deletion' : 'Delete'}
            aria-label={deleteLabel ? (isDeleteConfirmationPending ? `${deleteLabel}: click again to confirm` : deleteLabel) : undefined}
          >
            <span className={isDeleteConfirmationPending ? '-rotate-12 inline-flex' : 'inline-flex'}>
              <FontAwesomeIcon icon={faTrash} />
            </span>
            {isDeleteConfirmationPending ? <span className="absolute -right-2 -top-1 text-[10px] font-bold text-red-500">?</span> : null}
          </button>
        ) : null}
      </div>
    </div>
  );
}

interface IGovernanceOnboardingNewListItemProps {
  children: React.ReactNode;
  onDelete: () => void;
  onSave?: () => void;
  saveButtonType?: 'button' | 'submit';
  /** What the save button is called, when saving the row means something more specific. */
  saveLabel?: string;
}

export function GovernanceOnboardingNewListItem({
  children,
  onDelete,
  onSave,
  saveButtonType = 'button',
  saveLabel = 'Save',
}: IGovernanceOnboardingNewListItemProps) {
  const [isDeleteConfirmationPending, setIsDeleteConfirmationPending] = React.useState(false);

  const handleDeleteClick = () => {
    if (isDeleteConfirmationPending) {
      onDelete();
      return;
    }

    setIsDeleteConfirmationPending(true);
  };

  return (
    <div className="grid grid-cols-12 gap-4 items-center bg-white border border-dashed border-slate-300 p-4 rounded-xl">
      {children}
      <div className="col-span-12 md:col-span-2 flex justify-end text-slate-400 mt-2 md:mt-0 gap-3">
        {onSave ? (
          <button type={saveButtonType} onClick={onSave} className="hover:text-primary transition-colors" title={saveLabel}>
            <FontAwesomeIcon icon={faCheck} />
          </button>
        ) : null}
        <button
          type="button"
          onClick={handleDeleteClick}
          className="hover:text-red-500 transition-colors relative"
          title={isDeleteConfirmationPending ? 'Click again to confirm deletion' : 'Delete'}
        >
          <span className={isDeleteConfirmationPending ? '-rotate-12 inline-flex' : 'inline-flex'}>
            <FontAwesomeIcon icon={faTrash} />
          </span>
          {isDeleteConfirmationPending ? <span className="absolute -right-2 -top-1 text-[10px] font-bold text-red-500">?</span> : null}
        </button>
      </div>
    </div>
  );
}

export function GovernanceAddAnotherPageButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="flex items-center gap-2 text-primary font-semibold text-sm hover:underline" onClick={onClick}>
      <FontAwesomeIcon icon={faCirclePlus} />
      <span>Add another page</span>
    </button>
  );
}
